import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import {
  Email as EmailIcon,
  WorkOutline as WorkIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

import { type EmployeeDto, EmployeeStatus } from 'src/api/api_data';
import { positionLabelTr } from 'src/utils/labels';

interface EmployeePersonalInfoTabProps {
  employee: EmployeeDto;
}

export default function EmployeePersonalInfoTab({ employee }: EmployeePersonalInfoTabProps) {
  const InfoCard = ({ icon, title, value }: { icon: React.ReactNode; title: string; value: string }) => (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ color: 'primary.main' }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Kişisel Bilgiler
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<EmailIcon />}
            title="Email Adresi"
            value={employee.email}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<WorkIcon />}
            title="Pozisyon"
            value={positionLabelTr(employee.position) || 'Belirtilmemiş'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <InfoCard
            icon={<BusinessIcon />}
            title="Şube"
            value={employee.branchName || 'Atanmamış'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="caption" color="text.secondary">
                  Durum
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={
                      employee.status === EmployeeStatus.ACTIVE
                        ? 'Aktif'
                        : employee.status === EmployeeStatus.INACTIVE
                        ? 'Pasif'
                        : employee.status === EmployeeStatus.ON_LEAVE
                        ? 'İzinli'
                        : employee.status === EmployeeStatus.BANNED
                        ? 'Yasaklı'
                        : employee.status === EmployeeStatus.REQUIRES_VERIFICATION
                        ? 'Doğrulama Bekliyor'
                        : employee.status
                    }
                    color={
                      employee.status === EmployeeStatus.ACTIVE
                        ? 'success'
                        : employee.status === EmployeeStatus.ON_LEAVE
                        ? 'warning'
                        : employee.status === EmployeeStatus.BANNED
                        ? 'error'
                        : employee.status === EmployeeStatus.REQUIRES_VERIFICATION
                        ? 'warning'
                        : 'default'
                    }
                  />
                  <Chip
                    icon={employee.verified ? <CheckCircleIcon /> : <CancelIcon />}
                    label={employee.verified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                    color={employee.verified ? 'success' : 'default'}
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                İstatistikler
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="primary.main">
                    {employee.totalWorkHours?.toFixed(1) || '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Toplam Çalışma Saati
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="success.main">
                    {employee.attendanceCount || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Devam Günü
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="info.main">
                    {employee.attendanceCount && employee.totalWorkHours
                      ? (employee.totalWorkHours / employee.attendanceCount).toFixed(1)
                      : '0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ortalama Günlük Saat
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="h4" color="warning.main">
                    {employee.lastCheckInTime
                      ? new Date(employee.lastCheckInTime).toLocaleDateString('tr-TR')
                      : '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Son Giriş
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
